import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { updateFund } from 'lib/api/funds';
import { CompanyFund } from 'lib/api/funds/types';
import CustomButton from 'lib/designSystem/Button';
import PageHeader from 'lib/designSystem/PageHeader';
import { formatMoney, formatMoneyBack, showAlert } from 'lib/functions/utils';
import { buildValidation } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  fund: CompanyFund | undefined;
};

const initialState = {
  name: '',
  cost: 0,
  amount: 0,
};

const EditFund: React.FC<Props> = ({ open, setOpen, fund }) => {
  const dispatch = useDispatch();
  const { mutate, isPending } = updateFund(dispatch);
  const [form, setForm] = useState(initialState);

  const { hasError: nameHasError, message: nameErrorMessage } = buildValidation(
    !form.name.length,
    false,
    'Nombre obligatorio',
    '',
  );

  const { hasError: costHasError, message: costErrorMessage } = buildValidation(
    !form.cost,
    false,
    'Costo obligatorio',
    '',
  );

  const handleUpdate = async () => {
    if (!fund) return;
    if (nameHasError || costHasError) {
      return showAlert(
        dispatch,
        'error',
        `${nameErrorMessage} ${costErrorMessage}`,
      );
    }
    mutate(
      { id: fund.id, ...form },
      {
        onSuccess: () => setOpen(false),
      },
    );
  };

  useEffect(() => {
    if (fund) {
      setForm({
        name: fund.name ?? '',
        cost: fund.cost ?? 0,
        amount: fund.amount ?? 0,
      });
    }
  }, [fund, open]);

  if (!fund) return null;

  return (
    <div>
      <Dialog fullWidth open={open} onClose={() => setOpen(false)}>
        <DialogTitle>
          <PageHeader.TitleOnly
            iconType="edit"
            iconVariant="primary"
            title="Editar fondo"
            subtitle="Modifique los datos del fondo"
          />
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre"
            fullWidth
            margin="normal"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label="Rut"
            fullWidth
            margin="normal"
            value={fund.id}
            disabled
            helperText="El Rut no se puede modificar"
          />
          <TextField
            label="Costo"
            fullWidth
            margin="normal"
            type="number"
            inputProps={{ min: 0, max: 100, step: 0.01 }}
            value={form.cost || ''}
            onChange={(e) =>
              setForm({
                ...form,
                cost: Number(e.target.value),
              })
            }
          />
          <TextField
            label="Monto"
            fullWidth
            margin="normal"
            value={formatMoney(form.amount)}
            onChange={(e) => {
              const nonFormatted = formatMoneyBack(e.target.value);
              setForm({ ...form, amount: nonFormatted });
            }}
          />
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={() => setOpen(false)} color="secondary">
            Salir
          </CustomButton>
          <CustomButton
            onClick={() => handleUpdate()}
            color="primary"
            disabled={isPending}
            loading={isPending}
          >
            Actualizar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EditFund;
